/**
 * In-app browser detection + "Open in default browser" helpers.
 *
 * Why this exists: Google Sign-In refuses to run inside many social-media
 * in-app browsers (Instagram, Facebook, Messenger, Telegram, etc.) because
 * they are embedded WebViews. When Harday is opened from one of those apps we
 * nudge the user to reopen the page in their phone's real default browser,
 * where Google Login works reliably.
 *
 * This module ONLY inspects the User-Agent (plus a couple of well-known window
 * globals). It never touches the Google Login implementation, routing, or any
 * other product flow.
 */

const SESSION_DISMISS_KEY = 'hardayOpenInBrowserDismissed';

export const getUserAgent = () => {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || navigator.vendor || '';
};

/**
 * Harday's own Android APK WebView. It has a dedicated native Google login
 * flow, so it must never be treated as a "social in-app browser" and must
 * never see the Open-in-Browser modal.
 */
export const isHardayApkWebView = (ua = getUserAgent()) => {
  if (/HardayWebViewApp/i.test(ua)) return true;
  if (typeof window !== 'undefined') {
    if (window.HardayAndroid || window.AndroidInterface) return true;
    try {
      if (window.localStorage?.getItem('hardayClientSource') === 'android_apk') return true;
    } catch {
      /* localStorage can throw in privacy modes — ignore */
    }
  }
  return false;
};

/**
 * Ordered list of in-app browser signatures. First match wins, so more
 * specific apps (Messenger) are listed before the broader Facebook family.
 * `test` is matched against the User-Agent string.
 */
const IN_APP_SIGNATURES = [
  { name: 'Messenger', test: /\bFB_IAB\/(MESSENGER|Orca)|Messenger(ForiOS)?|\bOrca-Android\b/i },
  { name: 'Instagram', test: /\bInstagram\b/i },
  { name: 'Facebook', test: /\bFBAN\/|\bFBAV\/|\bFB_IAB\/|\bFB4A\b|\bFBIOS\b/i },
  { name: 'Telegram', test: /\bTelegram\b/i },
  { name: 'WhatsApp', test: /\bWhatsApp\b/i },
  { name: 'Snapchat', test: /\bSnapchat\b/i },
  { name: 'TikTok', test: /musical_ly|Bytedance|BytedanceWebview|\bTikTok\b/i },
  { name: 'Line', test: /\bLine\//i },
  { name: 'X', test: /\bTwitter\b/i },
  { name: 'LinkedIn', test: /\bLinkedInApp\b/i },
  { name: 'Pinterest', test: /\bPinterest\b/i },
  { name: 'WeChat', test: /MicroMessenger/i },
  { name: 'KakaoTalk', test: /KAKAOTALK/i },
];

/**
 * Real browsers we must always allow (never show the modal). These are checked
 * so that a UA which somehow also carries a generic "wv" token isn't mistaken
 * for an embedded browser. Named social apps above are detected first anyway.
 */
const KNOWN_REAL_BROWSER = /(SamsungBrowser|EdgA?|EdgiOS|OPR\/|Opera|Firefox|FxiOS|CriOS|DuckDuckGo|Brave|YaBrowser|UCBrowser|MiuiBrowser)/i;

/**
 * Generic Android embedded-WebView marker. Standalone Chrome does NOT include
 * "; wv)"; only embedded WebViews (which is how apps such as Telegram render
 * their in-app browser) do. Used as a fallback for apps that don't brand their
 * User-Agent.
 */
const ANDROID_WEBVIEW = /;\s*wv[);]/i;

export const getPlatform = (ua = getUserAgent()) => {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // iPadOS 13+ can masquerade as desktop Safari but still reports touch.
  if (/Macintosh/i.test(ua) && typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 1) {
    return 'ios';
  }
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

/**
 * Inspect the User-Agent and return whether we're inside a social in-app
 * browser, the friendly app name, and the platform.
 *
 * @returns {{ isInApp: boolean, appName: string, platform: 'ios'|'android'|'other' }}
 */
export const detectInAppBrowser = (ua = getUserAgent()) => {
  const platform = getPlatform(ua);
  const result = { isInApp: false, appName: '', platform };

  if (!ua) return result;

  // Never nag our own APK or a known real browser.
  if (isHardayApkWebView(ua)) return result;
  if (KNOWN_REAL_BROWSER.test(ua)) return result;

  const named = IN_APP_SIGNATURES.find((sig) => sig.test.test(ua));
  if (named) {
    return { isInApp: true, appName: named.name, platform };
  }

  // Fallback: an unbranded Android embedded WebView (e.g. Telegram, or a
  // niche social app). Only applies on Android — iOS uses SFSafariViewController
  // for most in-app browsing, which handles Google Login fine.
  if (platform === 'android' && ANDROID_WEBVIEW.test(ua)) {
    return { isInApp: true, appName: 'this app', platform };
  }

  return result;
};

/** Convenience boolean. */
export const isInAppBrowser = (ua = getUserAgent()) => detectInAppBrowser(ua).isInApp;

/**
 * Whether the current platform + app can attempt to auto-open the default
 * browser (Android intent), versus requiring manual instructions (iOS, or an
 * unknown platform).
 */
export const canAttemptAutoOpen = (platform = getPlatform()) => platform === 'android';

/**
 * Build an Android VIEW intent URL for the given https(s) URL. Navigating to
 * this hands the URL to Android's intent resolver, which opens it in the
 * device's default browser (or a chooser) — leaving the host app's WebView.
 *
 * We intentionally do NOT pin a package, so the user's *default* browser is
 * used rather than forcing Chrome.
 */
export const buildAndroidIntentUrl = (targetUrl = getCurrentUrl()) => {
  const cleaned = String(targetUrl).replace(/#.*$/, '');
  const match = cleaned.match(/^(https?):\/\/(.+)$/i);
  if (!match) return '';
  const scheme = match[1].toLowerCase();
  const rest = match[2];
  return `intent://${rest}#Intent;scheme=${scheme};action=android.intent.action.VIEW;end`;
};

export const getCurrentUrl = () => {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.href;
};

/**
 * Attempt to leave the in-app WebView and open the current page in the default
 * browser. Returns true if we could fire an attempt (Android), false if the
 * platform requires manual steps (iOS/other). Actual success cannot be known
 * synchronously — callers should watch for the page becoming hidden and fall
 * back to manual instructions if it doesn't.
 */
export const attemptOpenInDefaultBrowser = (targetUrl = getCurrentUrl()) => {
  if (!canAttemptAutoOpen()) return false;
  const intentUrl = buildAndroidIntentUrl(targetUrl);
  if (!intentUrl) return false;
  try {
    window.location.href = intentUrl;
    return true;
  } catch {
    return false;
  }
};

/* ------------------------------------------------------------------ *
 * Session dismissal — requirement: once the page is opened in the real
 * browser (or the user chooses to continue anyway), don't nag again for
 * this session.
 * ------------------------------------------------------------------ */

export const isDismissedForSession = () => {
  try {
    return window.sessionStorage?.getItem(SESSION_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

export const dismissForSession = () => {
  try {
    window.sessionStorage?.setItem(SESSION_DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
};

/**
 * Single entry point for the UI: should we show the Open-in-Browser modal?
 * True only when inside a social in-app browser AND not already dismissed for
 * this session. (When the page is opened in a normal browser, detection returns
 * false, so the modal never shows — satisfying the "don't show again" rule.)
 */
export const shouldShowOpenInBrowserModal = () => {
  if (isDismissedForSession()) return false;
  return isInAppBrowser();
};
